import {
  Network,
  ListTree,
  Upload,
  Play,
  Palette,
  Link2,
  Settings,
  SlidersHorizontal,
  Zap,
  Package,
  GitBranch,
  BarChart3,
  Radio,
  Grid,
  History,
  Grid3X3,
  Ruler,
  Mountain,
  Camera,
  Lightbulb,
  Activity,
  ShieldCheck,
  Weight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  MOBILE_TOOL_PANEL_PIN_IDS,
  type MobileToolPanelPinId,
} from "../constants/mobileToolPanelPins";
import { SceneBuilderIcon } from "../components/ui/Icons";

export type MobilePinnedPanelTabMeta = { Icon: LucideIcon; label: string };

const DEV_ONLY_PINS = new Set<MobileToolPanelPinId>([
  MOBILE_TOOL_PANEL_PIN_IDS.fileUpload,
  MOBILE_TOOL_PANEL_PIN_IDS.poseEditor,
  MOBILE_TOOL_PANEL_PIN_IDS.circleDetection,
  MOBILE_TOOL_PANEL_PIN_IDS.measurements,
  MOBILE_TOOL_PANEL_PIN_IDS.history,
  MOBILE_TOOL_PANEL_PIN_IDS.assetStats,
  MOBILE_TOOL_PANEL_PIN_IDS.meshEditor,
  MOBILE_TOOL_PANEL_PIN_IDS.xmlSource,
  MOBILE_TOOL_PANEL_PIN_IDS.warmstart,
  MOBILE_TOOL_PANEL_PIN_IDS.trainingPipeline,
  MOBILE_TOOL_PANEL_PIN_IDS.octoTraining,
  MOBILE_TOOL_PANEL_PIN_IDS.trainingMetrics,
  MOBILE_TOOL_PANEL_PIN_IDS.policyRollout,
]);

const TRAINING_ONLY_PINS = new Set<MobileToolPanelPinId>([
  MOBILE_TOOL_PANEL_PIN_IDS.behaviorEditor,
  MOBILE_TOOL_PANEL_PIN_IDS.ikTarget,
]);

export const MOBILE_PINNED_TOOL_PANEL_TAB_META: Record<
  MobileToolPanelPinId,
  MobilePinnedPanelTabMeta
> = {
  [MOBILE_TOOL_PANEL_PIN_IDS.sceneGraph]: { Icon: Network, label: "Scene Graph" },
  [MOBILE_TOOL_PANEL_PIN_IDS.sceneInventory]: { Icon: ListTree, label: "Scene Inventory" },
  [MOBILE_TOOL_PANEL_PIN_IDS.modelCheck]: { Icon: ShieldCheck, label: "Check Model" },
  [MOBILE_TOOL_PANEL_PIN_IDS.massProperties]: { Icon: Weight, label: "Mass" },
  [MOBILE_TOOL_PANEL_PIN_IDS.fileUpload]: { Icon: Upload, label: "File Upload" },
  [MOBILE_TOOL_PANEL_PIN_IDS.poseEditor]: { Icon: Play, label: "Pose Editor" },
  [MOBILE_TOOL_PANEL_PIN_IDS.materialEditor]: { Icon: Palette, label: "Material Editor" },
  [MOBILE_TOOL_PANEL_PIN_IDS.lights]: { Icon: Lightbulb, label: "Lights" },
  [MOBILE_TOOL_PANEL_PIN_IDS.ikTarget]: { Icon: Link2, label: "IK Target" },
  [MOBILE_TOOL_PANEL_PIN_IDS.runtimeControls]: {
    Icon: SlidersHorizontal,
    label: "Runtime",
  },
  [MOBILE_TOOL_PANEL_PIN_IDS.physics]: { Icon: Settings, label: "Settings" },
  [MOBILE_TOOL_PANEL_PIN_IDS.actuators]: { Icon: Zap, label: "Actuators" },
  [MOBILE_TOOL_PANEL_PIN_IDS.warmstart]: { Icon: Package, label: "Warm-start" },
  [MOBILE_TOOL_PANEL_PIN_IDS.behaviorEditor]: { Icon: GitBranch, label: "Behavior" },
  [MOBILE_TOOL_PANEL_PIN_IDS.trainingPipeline]: { Icon: Play, label: "Training Setup" },
  [MOBILE_TOOL_PANEL_PIN_IDS.octoTraining]: { Icon: Play, label: "Octo Training" },
  [MOBILE_TOOL_PANEL_PIN_IDS.trainingMetrics]: { Icon: BarChart3, label: "Training Metrics" },
  [MOBILE_TOOL_PANEL_PIN_IDS.policyRollout]: { Icon: Radio, label: "Policy Rollout" },
  [MOBILE_TOOL_PANEL_PIN_IDS.circleDetection]: { Icon: Grid, label: "Circle Detection" },
  [MOBILE_TOOL_PANEL_PIN_IDS.measurements]: { Icon: Ruler, label: "Measure" },
  [MOBILE_TOOL_PANEL_PIN_IDS.history]: { Icon: History, label: "History" },
  [MOBILE_TOOL_PANEL_PIN_IDS.assetStats]: { Icon: BarChart3, label: "Asset Stats" },
  [MOBILE_TOOL_PANEL_PIN_IDS.meshEditor]: { Icon: Grid3X3, label: "Mesh Manager" },
  [MOBILE_TOOL_PANEL_PIN_IDS.xmlSource]: { Icon: Upload, label: "XML Source" },
  [MOBILE_TOOL_PANEL_PIN_IDS.terrain]: { Icon: Mountain, label: "Terrain" },
  [MOBILE_TOOL_PANEL_PIN_IDS.objectLibrary]: { Icon: SceneBuilderIcon, label: "Library" },
  [MOBILE_TOOL_PANEL_PIN_IDS.cameraProbe]: { Icon: Camera, label: "Camera Probe" },
  [MOBILE_TOOL_PANEL_PIN_IDS.sensors]: { Icon: Activity, label: "Sensors" },
};

export function isMobilePinnedToolPanelIdInApp(
  id: MobileToolPanelPinId,
  opts: { showDevTools: boolean; showTrainingIkTools: boolean }
): boolean {
  if (DEV_ONLY_PINS.has(id)) return opts.showDevTools;
  if (TRAINING_ONLY_PINS.has(id)) return opts.showTrainingIkTools;
  return true;
}
